import { useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useBudgets } from "@/api/hooks";
import type { Budget, BudgetStatus } from "@/api/types";

const STATUS_LABELS: Record<BudgetStatus, string> = {
  draft: "Brouillon",
  active: "Actif",
  frozen: "Gelé",
  closed: "Clôturé",
};

const STATUS_COLORS: Record<BudgetStatus, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  frozen: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

export function BudgetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const fiscalYear = parseInt(searchParams.get("year") ?? String(currentYear), 10);
  const statusFilter = searchParams.get("status") as BudgetStatus | null;

  const { data: budgets, isLoading, error } = useBudgets(fiscalYear, statusFilter || undefined);

  const handleYearChange = useCallback(
    (year: number) => {
      const params = new URLSearchParams(searchParams);
      params.set("year", String(year));
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleStatusFilter = useCallback(
    (status: BudgetStatus | null) => {
      const params = new URLSearchParams(searchParams);
      if (status) {
        params.set("status", status);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const formatCurrency = (cents: string) => {
    const value = parseInt(cents, 10);
    return (value / 100).toFixed(2) + " €";
  };

  const totalAllocated =
    budgets?.reduce((sum, b) => sum + parseInt(b.allocatedAmountInCents, 10), 0) ?? 0;
  const totalUsed = budgets?.reduce((sum, b) => sum + parseInt(b.usedAmountInCents, 10), 0) ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Budgets</h1>
        <Link
          to="/acquisition/budgets/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        >
          <span>+</span>
          Nouveau budget
        </Link>
      </div>

      {/* Year Selector */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">Année fiscale :</span>
        <div className="flex gap-2">
          {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
            <button
              key={year}
              onClick={() => {
                handleYearChange(year);
              }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                fiscalYear === year
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Budget total alloué</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {formatCurrency(String(totalAllocated))}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Utilisé (engagé + dépensé)</p>
          <p className="mt-1 text-2xl font-semibold text-blue-600 dark:text-blue-400">
            {formatCurrency(String(totalUsed))}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">Disponible</p>
          <p className="mt-1 text-2xl font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(String(totalAllocated - totalUsed))}
          </p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            handleStatusFilter(null);
          }}
          className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
            !statusFilter
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Tous
        </button>
        {(["draft", "active", "frozen", "closed"] as BudgetStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => {
              handleStatusFilter(status);
            }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              statusFilter === status
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Une erreur est survenue lors du chargement.
        </div>
      ) : budgets && budgets.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {budgets.length} budget{budgets.length > 1 ? "s" : ""}
          </p>
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {budgets.map((budget) => (
              <BudgetRow key={budget.id} budget={budget} formatCurrency={formatCurrency} />
            ))}
          </div>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Aucun budget trouvé pour {fiscalYear}</p>
        </div>
      )}
    </div>
  );
}

function BudgetRow({
  budget,
  formatCurrency,
}: {
  budget: Budget;
  formatCurrency: (cents: string) => string;
}) {
  const getUsageColor = () => {
    if (budget.isOverspent) return "bg-red-500";
    if (budget.isOverWarningThreshold) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <Link
      to={`/acquisition/budgets/${budget.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-gray-900 dark:text-white">{budget.name}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[budget.status]}`}
            >
              {STATUS_LABELS[budget.status]}
            </span>
            {budget.isOverspent && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400">
                Dépassement
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{budget.code}</span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>{budget.locationName}</span>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Utilisé : {formatCurrency(budget.usedAmountInCents)}</span>
              <span>{budget.usagePercent.toFixed(1)}%</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full transition-all ${getUsageColor()}`}
                style={{ width: `${String(Math.min(budget.usagePercent, 100))}%` }}
              />
            </div>
          </div>
        </div>

        <div className="ml-4 flex items-center gap-4 text-sm">
          <div className="text-right">
            <p className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(budget.allocatedAmountInCents)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">alloué</p>
          </div>
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

export default BudgetsPage;
